type ReportDocumentNestedBlock<Item> =
  | { kind: 'item'; item: Item }
  | { kind: 'columns'; items: Item[] };

export type ReportDocumentNestedPage<Item> = {
  id: string;
  blocks: ReportDocumentNestedBlock<Item>[];
};

const REPORT_DOCUMENT_MIN_COLUMNS = 2;
const REPORT_DOCUMENT_MAX_COLUMNS = 3;

export const REPORT_DOCUMENT_SCHEMA_VERSION = 2;

type ReportDocumentLayoutViolationCode =
  | 'duplicate-item-id'
  | 'duplicate-page-id'
  | 'columns-size'
  | 'no-pages';

export type ReportDocumentLayoutViolation = {
  code: ReportDocumentLayoutViolationCode;
  message: string;
  itemId?: string;
  pageId?: string;
};

export const validateReportDocumentLayout = (
  pages: readonly ReportDocumentNestedPage<{ id: string }>[]
): ReportDocumentLayoutViolation[] => {
  const violations: ReportDocumentLayoutViolation[] = [];
  if (pages.length === 0) {
    violations.push({
      code: 'no-pages',
      message: 'A report document needs at least one page',
    });
  }

  const pageIds = new Set<string>();
  const itemIds = new Set<string>();
  for (const page of pages) {
    if (pageIds.has(page.id)) {
      violations.push({
        code: 'duplicate-page-id',
        pageId: page.id,
        message: `Page '${page.id}' appears more than once`,
      });
    }
    pageIds.add(page.id);

    for (const block of page.blocks) {
      if (
        block.kind === 'columns' &&
        (block.items.length < REPORT_DOCUMENT_MIN_COLUMNS ||
          block.items.length > REPORT_DOCUMENT_MAX_COLUMNS)
      ) {
        violations.push({
          code: 'columns-size',
          pageId: page.id,
          message: `A columns block on page '${page.id}' holds ${block.items.length} items; expected ${REPORT_DOCUMENT_MIN_COLUMNS} to ${REPORT_DOCUMENT_MAX_COLUMNS}`,
        });
      }
      const items = block.kind === 'columns' ? block.items : [block.item];
      for (const item of items) {
        if (itemIds.has(item.id)) {
          violations.push({
            code: 'duplicate-item-id',
            itemId: item.id,
            pageId: page.id,
            message: `Item '${item.id}' is placed more than once`,
          });
        }
        itemIds.add(item.id);
      }
    }
  }

  return violations;
};
